<template>
  <div class="flex-grow-1 d-flex flex-column overflow-y-hidden">
    <div class="a-header">
      <span class="a-header--title slide-down-enter-active">
        View Webpages
      </span>
      <a-space>
        <a-button size="large" class="a-header--button slide-down-enter-active" type="default" v-on:click="downloadCsv">
          <a-icon type="download" theme="outlined" />
          Download CSV
        </a-button>
        <a-button size="large" class="a-header--button slide-down-enter-active" type="primary">
          <router-link :to="'/domain/add-webpage'">
            <a-icon type="plus-square" theme="filled" />
            Add New
          </router-link>
        </a-button>
      </a-space>
    </div>
    <div v-if="isLoading" class="d-flex justify-content-center align-items-center pt-35-px">
      <Spinner />
    </div>
    <transition name="slide-up">
      <div v-if="!isLoading" class="a-filter">
        <a-row class="full-w" type="flex" justify="start" :gutter="24">
          <a-col :span="6">
            <div class="full-w a-input-group" :class="{ active: !!filterParams.search }">
              <a-button size="large">
                <a-icon type="search" />
              </a-button>
              <a-input class="custom-prefix" placeholder="Search" @input="onSearch" />
            </div>
          </a-col>
          <a-col :span="6">
            <div
              class="full-w a-input-group"
              :class="{
                active: !!filterParams.fromDate && !!filterParams.toDate,
              }"
            >
              <a-button size="large">
                <a-icon type="filter" />
              </a-button>
              <a-range-picker :ranges="RANGE" :placeholder="['From', 'To']" :format="DATE_FORMAT.DDMMMYYYY" @change="onChangeDate" />
            </div>
          </a-col>
          <a-col :span="6">
            <div class="full-w a-select-group" :class="{ active: !!filterParams.status }">
              <a-button size="large">
                <a-icon type="filter" />
              </a-button>
              <a-select v-model="filterParams.status" :size="'large'" placeholder="Status" @change="onChangeStatus">
                <a-select-option v-for="status in statusList" :key="status">
                  {{ status }}
                </a-select-option>
              </a-select>
            </div>
          </a-col>
          <a-col :span="6">
            <div class="full-w a-select-group" :class="{ active: !!filterParams.status }">
              <a-button size="large">
                <a-icon type="filter" />
              </a-button>
              <a-select v-model="filterParams.monitoringType" :size="'large'" placeholder="Monitoring Type" @change="onChangeMonitoringTypeFilter">
                <a-select-option v-for="monitoringType in monitoringList" :key="monitoringType">
                  {{ monitoringType }}
                </a-select-option>
              </a-select>
            </div>
          </a-col>
        </a-row>
        <a-row class="full-w mt-2" type="flex" justify="start">
          <a-button size="default" :disabled="!isHasSelected" type="danger" class="danger-btn" @click="onDeletes()"> <a-icon type="delete" /> Delete </a-button>
          <a-button size="default" :disabled="!isHasSelected" class="ml-2 warning-btn" @click="onMultipleReBaseline()">
            <img v-if="!isHasSelected" class="mr-2" src="@assets/icon/off.svg" />
            <img v-if="isHasSelected" class="mr-2" src="@assets/icon/OffHover.svg" />
            Rebaseline
          </a-button>
          <!-- Wrap button in span to prevent Tooltip from hijacking classes on disabled buttons -->
          <!-- https://github.com/ant-design/ant-design/issues/21404#issuecomment-586800984 -->
          <a-tooltip title="Detects external third-party scripts and suggests policy rules to reduce false positives." placement="top">
            <span class="ml-2">
              <a-button
                size="default"
                class="policy-btn"
                :disabled="!isHasSelected || isSuggestingPolicy"
                :loading="isSuggestingPolicy"
                @click="onSuggestPolicy"
              >
                <a-icon v-if="!isSuggestingPolicy" type="bulb" />
                Suggest Policy
              </a-button>
            </span>
          </a-tooltip>
        </a-row>
      </div>
    </transition>
    <div v-resize="elSizeChange" class="a-body overflow-y-hidden flex-grow-1">
      <transition name="slide-up">
        <template v-if="!isLoading">
          <PaginationTable
            :hasSelectRow="true"
            :pagination="pagin"
            :columns="DomainWebpageColumn"
            :dataSource="filterData"
            :tableSize="tableSize"
            @changeSelectedRow="onSelectChange"
          >
            <div slot="urls" slot-scope="{ record }">
              <router-link :to="'/domain/' + domain.id + '/webpages/' + record.id" class="url text-wrap">
                {{ record.url_name }}
              </router-link>
            </div>
            <div slot="last_verify" slot-scope="{ record }">
              <p class="time-text text-truncate">
                <time>
                  {{ record.last_verify | dateString }}
                </time>
              </p>
              <p class="time-text text-truncate">
                <time>
                  {{ record.last_verify | timeString }}
                </time>
              </p>
            </div>
            <template slot="operation" slot-scope="{ record }">
              <a-dropdown placement="topLeft">
                <a-icon class="operation" type="ellipsis" />
                <a-menu slot="overlay" class="operation-menu">
                  <a-menu-item v-if="record.health_status !== `Baseline Incomplete`" @click="onEdit(record)">
                    <img class="mr-2" src="@assets/icon/operation.svg" />
                    Configuration
                  </a-menu-item>
                  <a-menu-item v-if="record.health_status !== `Awaiting Verification` && record.health_status !== `Baseline Incomplete`">
                    <router-link :to="'webpages/' + record.id + '/response-performance'">
                      <img class="mr-2" src="@assets/icon/report.svg" />
                      Performance
                    </router-link>
                  </a-menu-item>
                  <a-menu-item>
                    <div class="full-wh" @click="showDeleteConfirm(record)">
                      <img class="mr-2" src="@assets/icon/delete.svg" />
                      Delete
                    </div>
                  </a-menu-item>
                </a-menu>
              </a-dropdown>
            </template>
            <template slot="status" slot-scope="{ record }">
              <a-tag :color="STATUS_COLOR[record.health_status]">
                {{ record.health_status }}
              </a-tag>
            </template>
            <template slot="plan_type" slot-scope="{ record }">
              {{ record.plan_type | lowercase | capitalize }}
            </template>
            <span slot="polling_interval" slot-scope="{ record }">
              <template v-if="record.ver_type==1">Variable</template>
              <template v-else>{{ POLLING_INTERVAL[record.polling_interval] | timeText }}</template>
            </span>
            <template slot="monitoring_type" slot-scope="{ record }">
              <template v-if="record.ver_type==1">Variable</template>
              <template v-else>Committed</template>
            </template>
          </PaginationTable>
        </template>
      </transition>
    </div>
    <div class="a-footer">
      <PaginationBar :pageSize.sync="pagin.pageSize" :total="pagin.total" @change="paginChange" />
    </div>
    <a-modal
      v-model="isShowMultipleBaseline"
      style="top: 20px;"
      width="80vw"
      class="rebaseline-modal"
      title="Rebaseline Webpages"
      cancelText="Close"
      okText="Submit Batch Job"
      :cancelButtonProps="{ style: { float: 'right' } }"
      :okButtonProps="{
        style: {
          marginRight: '8px',
          opacity: isSuccessMultipleBaseline ? '0' : '1',
        },
      }"
      :afterClose="afterCloseReBaselineModal"
      :closable="false"
      :maskClosable="false"
      @ok="submitMultipleRebaseline"
    >
      <div v-if="!isSuccessMultipleBaseline">
        <PaginationTable
          :columns="MultipleReBaselineColumns"
          :dataSource="multipleReBaselineData"
        >
          <template slot="webpageUrl" slot-scope="{ record }">
            <a class="url" :href="record.url" target="_blank" rel="noopener noreferrer">
              {{ record.url }}
            </a>
          </template>
          <template slot="healthStatus" slot-scope="{ record }">
            <a-tag :color="STATUS_COLOR[record.health_status]">
              {{ record.health_status }}
            </a-tag>
          </template>
          <template slot="lastVerify" slot-scope="{ record }">
            <p class="time-text">
              <time>
                {{ record.last_verify | dateString }}
              </time>
            </p>
            <p class="time-text">
              <time>
                {{ record.last_verify | timeString }}
              </time>
            </p>
          </template>
          <template slot="planType" slot-scope="{ record }">
            <p class="mb-0">
              {{ record.plan_type | lowercase | capitalize }}
            </p>
          </template>
        </PaginationTable>
      </div>
      <div v-if="isSuccessMultipleBaseline">
        <div class="success-rebaseline-container">
          <div>
            <a-icon type="check-circle" theme="filled" class="check-success" />
          </div>
          <div class="ml-3">
            <p class="mb-1 notice-text">{{ multipleReBaselineData.length }} webpages has been queued for rebaseline.</p>
            <p class="mb-1 notice-text">
              This may take a few minutes to a few hours depending on the number of pages.
            </p>
            <p class="mb-1 notice-text">
              You may check the status of this batch job
              <span>
                <router-link
                  class="nav-link-detail"
                  :to="{
                    path: '/batch-jobs/detail/RB' + batchIdsuccessMultipleBaseResponse,
                  }"
                >
                  ID
                  {{ 'RB' + batchIdsuccessMultipleBaseResponse }} at this link.
                </router-link>
              </span>
            </p>
            <p class="mb-0 notice-text">An email will be sent to {{ parentUserName }} when the job is finished.</p>
          </div>
        </div>
      </div>
    </a-modal>
    <SuggestedPolicyModal
      :visible="showSuggestedPolicyModal"
      :groups="suggestedPolicyGroups"
      :allPolicies="allPoliciesForSuggestion"
      @close="showSuggestedPolicyModal = false"
    />
  </div>
</template>

<script>
  import { debounce } from 'lodash';
  import { RANGE } from '@constants/range';
  import { DATE_FORMAT } from '@constants/dateFormat';
  import { STATUS_COLOR } from '@constants/statusColor';
  import { POLLING_INTERVAL } from '@constants/pollingInterval';
  import { DomainWebpageColumn } from '../columnsTable/DomainWebpageColumn';
  import { MultipleReBaselineColumns } from '../../Webpages/columnsTable/MultipleReBaselineColumns';
  import WebpageService from '../../Webpages/services/webpage.service';
  import DomainService from "../services/Domain.service";
  import AccountService from '../../Account/services/account.service';
  import SuggestedPolicyModal from '@shared/components/SuggestedPolicy/SuggestedPolicyModal.vue';
  import suggestedPolicyMixin from '@shared/mixins/suggestedPolicy.mixin.js';

  const ALL_STATUS = 'All Status';
  const statusWebpages = Object.keys(STATUS_COLOR)
    .filter(key => key !== 'Archived')
    .map(key => key.toString().toLowerCase())
    .map(key => `${key.charAt(0).toUpperCase()}${key.substr(1)}`);
  const statusList = [ALL_STATUS, ...statusWebpages];
  const monitoringList = ['All Monitoring Types', 'Committed', 'Variable'];

  export default {
    name: 'DomainWebpageList',
    mixins: [suggestedPolicyMixin],
    components: {
      SuggestedPolicyModal,
    },
    data() {
      let pageSize = Number(window.localStorage.getItem("pageSize"));
      if(!pageSize) {
        pageSize = 5;
      }

      return {
        RANGE,
        ALL_STATUS,
        DATE_FORMAT,
        STATUS_COLOR,
        POLLING_INTERVAL,
        statusList,
        monitoringList,
        MultipleReBaselineColumns,
        data: [],
        filterData: [],
        DomainWebpageColumn,
        filterParams: {
          search: '',
          fromDate: null,
          toDate: null,
        },
        pagin: {
          current: 1,
          pageSize: pageSize,
          total: 0,
        },
        selectedRowKeys: [],
        visibleModal: false,
        isHandledChartData: false,
        chartData: {},
        timeUnit: 'hour',
        timeUnitStepSize: 2,
        performanceData: [],
        performanceWebpageName: '',
        performanceWebpageUrl: '',
        filterChartOptions: {},
        chartWidth: 'auto',
        isShowMultipleBaseline: false,
        multipleReBaselineData: [],
        isSuccessMultipleBaseline: false,
        batchIdsuccessMultipleBaseResponse: '',
        currentUserName: '',
        parentUserName: '',
        tableScrollHeight: 500,
        isLoading: true,
        tableSize: {
          height: '',
          width: '',
        },
        domain: {
          id: +this.$route.params.id,
        },
      };
    },
    computed: {
      isHasSelected() {
        return this.selectedRowKeys.length > 0;
      },
    },
    mounted() {
      this.getList();
      this.getParentUser();
      this.currentUserName = (this.$store.state && this.$store.state.user && this.$store.state.user.data && this.$store.state.user.data.username) || '';
    },
    methods: {
      similarCompare(fisrtString, secondString) {
        const lengthFirstString = fisrtString.length;
        const lengtgSecondString = secondString.length;
        const maxLength = Math.max(lengthFirstString, lengtgSecondString);
        const minLength = Math.min(lengthFirstString, lengtgSecondString);
        let percentSimilar = 0;
        for (let iterable = 0; iterable < minLength; iterable++) {
          if (fisrtString[iterable].toLowerCase() === secondString[iterable].toLowerCase()) {
            percentSimilar++;
          }
        }
        return (percentSimilar / maxLength) * 100;
      },
      filter() {
        let filterResult = [];
        const { search, fromDate, toDate, status, monitoringType } = this.filterParams;
        filterResult =
          search && !!search.trim()
            ? this.data.filter(item => {
                const includeWebUrl = (item.url_name || '').toString().toLowerCase().includes(search);
                const includeWebName = (item.config_name || '').toString().toLowerCase().includes(search);
                const includePlanType = (item.plan_type || '').toString().toLowerCase().includes(search);
                const includePollingIntervalTime = (item.polling_interval || '').toString().toLowerCase().includes(search);
                return includeWebUrl || includeWebName || includePlanType || includePollingIntervalTime;
              })
            : this.data;
        filterResult = fromDate ? filterResult.filter(item => new Date(item.last_verify).getTime() >= fromDate) : filterResult;
        filterResult = toDate ? filterResult.filter(item => new Date(item.last_verify).getTime() <= toDate) : filterResult;
        filterResult = !status || status === ALL_STATUS ? filterResult : filterResult.filter(item => this.similarCompare(item.health_status || '', status) >= 80);

        if (monitoringType) {
          if (monitoringType == 'Variable') {
            filterResult = filterResult.filter(item => item.ver_type === 1);
          } else if (monitoringType == 'Committed') {
            filterResult = filterResult.filter(item => item.ver_type === 0);
          }
          // Otherwise show all monitoring types
        }

        this.filterData = filterResult;
        this.pagin.total = filterResult.length;
      },
      onMultipleReBaseline() {
        this.multipleReBaselineData = [...this.data].filter(item => this.selectedRowKeys.includes(item.id));
        this.isShowMultipleBaseline = true;
      },
      getPagesForSuggestedPolicy() {
        return [...this.data].filter(item => this.selectedRowKeys.includes(item.id));
      },
      getSuggestedPolicyEmptyMessage() {
        return 'No selected webpages found.';
      },
      onDeletes() {
        const recordSeleted = this.data.filter(item => this.selectedRowKeys.includes(item.id));
        Promise.all(
          recordSeleted.map(async site => {
            await WebpageService.delete(site.id);
          }),
        ).then(() => {
          this.$showSuccessMessage('Deleted webpages');
          this.getList();
        });
      },
      onDelete(record) {
        WebpageService.delete(record.id).then(() => {
          this.$showSuccessMessage('Deleted webpage');
          this.getList();
        });
      },
      onEdit(record) {
        this.$router.push({
          path: '/domain/' + this.domain.id + '/webpages/' + record.id,
        });
      },
      onSelectChange(selectedRowKeys) {
        this.selectedRowKeys = selectedRowKeys;
      },
      onSearch: debounce(function (event) {
        const searchTerm = (event.target.value && String(event.target.value).trim().toLowerCase()) || '';
        this.filterParams.search = searchTerm;
        this.filter();
      }, 800),
      onCancel() {
        this.$router.push('/webpages/list');
      },
      paginChange(pagin) {
        this.pagin = { ...pagin };
      },
      onChangeStatus(value) {
        this.filterParams.status = value;
        this.filter();
      },
      onChangeMonitoringTypeFilter(value) {
        this.filterParams.monitoringType = value;
        this.filter();
      },
      showDeleteConfirm(record) {
        this.$confirm({
          title: 'Are you sure delete this item?',
          okText: 'Yes',
          okType: 'danger',
          cancelText: 'No',
          onOk: () => {
            this.onDelete(record);
          },
        });
      },
      async getList() {
        try {
          const responseData = await DomainService.fetchSubscribedDomainWebpagesById(this.domain.id);
          this.data = (responseData || []).map((item, index) => ({
            ...item,
            index: index + 1,
          }));
          this.filter();
        } catch {
          this.$showErrorMessage('HTTP error received from web server.!');
        } finally {
          if (this.isLoading) {
            setTimeout(() => {
              this.isLoading = false;
            }, 300);
          }
        }
      },
      onChangeDate(dates, dateStrings) {
        this.filterParams.fromDate = new Date(dateStrings[0]).setHours(0, 0, 0, 1);
        this.filterParams.toDate = new Date(dateStrings[1]).setHours(23, 59, 59, 59);
        this.filter();
      },
      handleOk() {
        this.visibleModal = false;
      },
      setRowKeyMultipleReBaseline(record, index) {
        return record.id || index;
      },
      async submitMultipleRebaseline() {
        const urlIds = this.multipleReBaselineData.map(item => item.id);
        try {
          const successResponse = await WebpageService.reBaselineMultipleWebpages(urlIds);
          this.batchIdsuccessMultipleBaseResponse = successResponse.id;
          this.isSuccessMultipleBaseline = true;
          this.selectedRowKeys = [];
        } catch {
          this.isShowMultipleBaseline = false;
        }
      },
      afterCloseReBaselineModal() {
        this.isSuccessMultipleBaseline = false;
      },
      elSizeChange(size) {
        this.tableSize = size;
      },
      async getParentUser() {
        const data = await AccountService.getUser();
        this.parentUserName = data.name;
        return data;
      },
      async downloadCsv() {
        const responseData = await DomainService.fetchSubscribedDomainWebpagesById(this.domain.id);
        //console.log(responseData);
        let link = document.createElement('a');

        let csvData = [];
        let csvHeader = ['No.', 'Webpage Name', 'Webpage URL', 'Plan Type', 'Polling Interval', 'Last Verified (UTC)', 'Status'];
        csvData.push(csvHeader);
        for (let i = 0; i < responseData.length; i++) {
          const entry = responseData[i];
          csvData.push([i + 1, entry.config_name, entry.url_name, entry.plan_type, entry.polling_interval, entry.last_verify, entry.health_status]);
        }

        // Format data into string
        let csvDataStringRows = [];
        for (let row of csvData) {
          //console.log(row);
          row = row.map(val => {
            if (val == null) {
              return '""';
            } else {
              let valReplacedSpecialChars = val.toString().replace(/[\r\n\t]/gmi, "").replace('"', '""');
              return '"' + valReplacedSpecialChars + '"';
            }
          });
          csvDataStringRows.push(row.join(","));
        }
        let csvDataString = csvDataStringRows.join("\n");

        link.href = window.URL.createObjectURL(new Blob([csvDataString], {type: 'text/csv;charset=utf-8;'}));
        link.setAttribute('download', "webpages.csv");
        document.body.appendChild(link);
        link.click();
        window.setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(link.href);
        }, 1000);
      }
    }
  };
</script>

<style lang="scss" scoped>
  .url {
    display: block;
  }
  .time-text {
    margin-bottom: 0;
  }
  .danger-btn {
    border-color: transparent;
    &:hover {
      border-color: transparent;
    }
    &:not([disabled]) {
      color: #f5f5f5;
      background-color: #f5222d;
    }
  }
  .warning-btn {
    border-color: transparent;
    &:hover {
      border-color: transparent;
    }
    &:not([disabled]) {
      color: #f5f5f5;
      background-color: #f1c40f;
    }
  }
  .policy-btn {
    border-color: transparent;
    &:hover {
      border-color: transparent;
    }
    &:not([disabled]) {
      color: #f5f5f5;
      background-color: #1890ff;
    }
  }
  .text-right {
    text-align: right;
  }
  .performance-chart {
    overflow-y: auto;
    padding: 0 0 20px;
    &::-webkit-scrollbar {
      height: 4px;
    }
  }
  .check-success {
    font-size: 40px;
    color: rgb(126, 207, 66);
  }
  .success-rebaseline-container {
    display: flex;
    align-items: center;
    .notice-text {
      font-size: 1rem;
    }
  }
</style>
